<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Patient Management' : 'Deleted Patients'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all patients in the system' : 'Restore or permanently delete patients'"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <template v-if="isDeletePage() != true">
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            color="grey darken-1"
                            outlined
                            small
                            class="mr-2 px-3"
                            v-bind="attrs"
                            v-on="on"
                            @click="openTrash"
                          >
                            <v-icon x-small>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>View Deleted Patients</span>
                      </v-tooltip>
                      <v-btn
                        v-if="users.length > 0"
                        color="success"
                        outlined
                        small
                        class="mr-2 px-3"
                        :loading="isExcelLoading"
                        @click="generateExcel"
                      >
                        <v-icon left x-small>mdi-microsoft-excel</v-icon>
                        Export
                      </v-btn>
                      <v-btn
                        color="primary"
                        elevation="1"
                        small
                        to="/patient/create"
                        class="mr-2 px-3"
                      >
                        <v-icon left x-small>mdi-plus</v-icon>
                        Add Patient
                      </v-btn>
                    </template>
                    <v-btn
                      v-else
                      color="grey darken-1"
                      outlined
                      small
                      class="mr-2 px-3"
                      @click="closeTrash"
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back to Patients
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Patients</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Patients"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Name, email, phone..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Customer Filter -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filter.customer_search"
                    label="Search Customer"
                    prepend-inner-icon="mdi-account-search"
                    outlined
                    dense
                    clearable
                    placeholder="Customer name, phone, email..."
                    hide-details
                    @click:clear="(filter.customer_search = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Patients Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-account-heart</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Patients List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-account-heart</v-icon>
                      {{ count }} {{ count === 1 ? "Patient" : "Patients" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading patients..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="users"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Avatar & Name Column -->
                <template #[`item.user_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.image_path"
                        :src="getImageUrl(item.image_path)"
                        :alt="item.first_name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1" size="18"
                        >mdi-account-heart</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ getFullName(item) }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.user_name || "No username" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Customer Column -->
                <template #[`item.customer_info`]="{ item }">
                  <div v-if="item.parent_patient">
                    <div class="text-body-2 font-weight-medium">
                      {{ item.parent_patient.user_name || (item.parent_patient.first_name + ' ' + item.parent_patient.last_name) }}
                    </div>
                    <div class="d-flex align-center mt-1" v-if="item.parent_patient.phone">
                      <v-icon x-small class="mr-1 grey--text">mdi-phone</v-icon>
                      <span class="text-caption">{{ item.parent_patient.phone }}</span>
                    </div>
                    <div class="d-flex align-center mt-1" v-if="item.parent_patient.email">
                      <v-icon x-small class="mr-1 grey--text">mdi-email</v-icon>
                      <span class="text-caption">{{ item.parent_patient.email }}</span>
                    </div>
                  </div>
                  <span v-else class="text-caption grey--text">—</span>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/patient/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Patient</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            v-bind="attrs"
                            v-on="on"
                            @click="openDeleteDialog(item)"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Patient</span>
                      </v-tooltip>

                      <!-- Blocked Doctors Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="grey darken-2"
                            outlined
                            class="ml-1"
                            :to="{
                              path: `/patients/${encryptIt(item.id)}/blocked-doctors`,
                              query: { patient_name: item.user_name || `${item.first_name} ${item.last_name}` },
                            }"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-account-cancel-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Blocked Doctors</span>
                      </v-tooltip>

                      <!-- Referrals Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="grey darken-2"
                            outlined
                            class="ml-1"
                            :to="{
                              path: `/patients/${encryptIt(item.id)}/referrals`,
                              query: { patient_name: item.user_name || `${item.first_name} ${item.last_name}` },
                            }"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-account-group</v-icon>
                          </v-btn>
                        </template>
                        <span>Referrals</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="openRestoreConfirm(item)"
                            v-bind="attrs"
                            v-on="on"
                            class="mr-1"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore this patient</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="openPermanentDeleteConfirm(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-delete-forever</v-icon>
                            Permanent Delete
                          </v-btn>
                        </template>
                        <span>Permanently delete this patient — cannot be undone</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-account-heart</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Patients Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No patients match your current filters. Try adjusting your search criteria."
                          : "You haven't added any patients yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/patient/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Patient
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="4"
                      circle
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <DeleteConfirmationDialog
        v-model="deleteDialog"
        :item="itemToDelete"
        :loading="deleteLoading"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      />
      <!-- Restore Confirmation Dialog -->
      <v-dialog v-model="showRestoreDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="success--text py-3 px-4">
            <v-icon color="success" left>mdi-restore</v-icon>
            Restore Patient
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4 pb-2 px-4">
            <p class="mb-2">This patient will be restored and become active again:</p>
            <div v-if="restoreTarget" class="grey lighten-4 rounded pa-3 mb-2">
              <div><strong>{{ (restoreTarget.first_name || '') + ' ' + (restoreTarget.last_name || '') || restoreTarget.user_name }}</strong></div>
              <div class="text-caption grey--text">{{ restoreTarget.email }}</div>
              <div class="text-caption grey--text">{{ restoreTarget.phone }}</div>
            </div>
            <v-alert v-if="restoreError" dense text type="error" class="mt-3 mb-0">{{ restoreError }}</v-alert>
          </v-card-text>
          <v-card-actions class="px-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn text @click="showRestoreDialog = false; restoreTarget = null; restoreError = ''" :disabled="isRestoring">Cancel</v-btn>
            <v-btn color="success" @click="confirmRestore" :loading="isRestoring">
              <v-icon left small>mdi-restore</v-icon>
              Restore
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Permanent Delete Confirmation Dialog -->
      <v-dialog v-model="showPermanentDeleteDialog" max-width="420" persistent>
        <v-card>
          <v-card-title class="error--text py-3 px-4">
            <v-icon color="error" left>mdi-delete-forever</v-icon>
            Permanently Delete Patient
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4 pb-2 px-4">
            <p class="mb-2">This action <strong>cannot be undone</strong>. The following patient will be permanently removed:</p>
            <div v-if="permanentDeleteTarget" class="grey lighten-4 rounded pa-3 mb-2">
              <div><strong>{{ (permanentDeleteTarget.first_name || '') + ' ' + (permanentDeleteTarget.last_name || '') || permanentDeleteTarget.user_name }}</strong></div>
              <div class="text-caption grey--text">{{ permanentDeleteTarget.email }}</div>
              <div class="text-caption grey--text">{{ permanentDeleteTarget.phone }}</div>
            </div>
            <p class="text-caption error--text mb-0">Their phone and email will be freed for re-registration.</p>
            <v-alert v-if="permanentDeleteError" dense text type="error" class="mt-3 mb-0">{{ permanentDeleteError }}</v-alert>
          </v-card-text>
          <v-card-actions class="px-4 pb-3">
            <v-spacer></v-spacer>
            <v-btn text @click="showPermanentDeleteDialog = false; permanentDeleteTarget = null; permanentDeleteError = ''" :disabled="isPermanentDeleting">Cancel</v-btn>
            <v-btn color="error" @click="confirmPermanentDelete" :loading="isPermanentDeleting">
              <v-icon left small>mdi-delete-forever</v-icon>
              Permanently Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Patient Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="480" persistent>
        <v-card>
          <v-card-title class="error white--text py-3 px-4">
            <v-icon left color="white">mdi-delete-alert</v-icon>
            Delete Patient
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="mb-2" v-if="patientToDelete">
              You are about to delete patient
              <strong>{{ getFullName(patientToDelete) }}</strong>.
            </p>
            <p class="text-caption red--text text--darken-2 mb-3">
              This action will soft-delete the patient and can be restored later from Trash.
            </p>
            <v-textarea
              v-model="deleteReason"
              label="Reason for deletion *"
              placeholder="Enter why this patient is being deleted (required)"
              outlined
              rows="3"
              :rules="[(v) => !!(v && v.trim()) || 'Reason is required']"
              :error-messages="deleteReasonError"
              hide-details="auto"
              autofocus
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="closeDeleteDialog" :disabled="isDeletingPatient">Cancel</v-btn>
            <v-btn
              color="error"
              depressed
              :loading="isDeletingPatient"
              @click="confirmDeletePatient"
            >
              <v-icon left small>mdi-delete</v-icon>
              Confirm Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- User Addresses Dialog -->
      <v-dialog v-model="showAddressDialog" max-width="800" scrollable>
        <v-card>
          <v-card-title class="py-3 px-4">
            <div class="d-flex align-center w-100">
              <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
              <div class="flex-grow-1">
                <h3 class="text-h6 font-weight-bold">Patient Addresses</h3>
                <p class="text-caption grey--text mb-0" v-if="selectedPatient">
                  {{ getFullName(selectedPatient) }}
                </p>
              </div>
              <v-btn icon @click="closeAddressDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-0" style="max-height: 500px;">
            <!-- Loading State -->
            <div v-if="loadingAddresses" class="d-flex justify-center align-center py-12">
              <div class="text-center">
                <health-loader message="Loading addresses..." />
              </div>
            </div>

            <!-- Addresses List -->
            <div v-else-if="patientAddresses && patientAddresses.length > 0" class="pa-4">
              <v-row>
                <v-col 
                  cols="12" 
                  md="6" 
                  v-for="(address, index) in patientAddresses" 
                  :key="address.id"
                >
                  <v-card elevation="2" class="address-card">
                    <v-card-title class="py-2 px-3">
                      <div class="d-flex align-center w-100">
                        <v-icon small color="primary" class="mr-2">mdi-home</v-icon>
                        <span class="text-subtitle-2">Address {{ index + 1 }}</span>
                        <v-spacer></v-spacer>
                        <v-chip 
                          v-if="address.is_primary" 
                          color="success" 
                          x-small 
                          outlined
                        >
                          Primary
                        </v-chip>
                      </div>
                    </v-card-title>

                    <v-card-text class="py-2 px-3">
                      <div class="address-details">
                        <!-- Receiver Name -->
                        <div v-if="address.receiver_name" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-account</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">Name:</span>
                            <span class="text-caption font-weight-medium ml-1">{{ address.receiver_name }}</span>
                          </div>
                        </div>

                        <!-- Receiver Phone -->
                        <div v-if="address.receiver_phone" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-phone</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">Phone:</span>
                            <span class="text-caption ml-1">{{ address.receiver_phone }}</span>
                          </div>
                        </div>

                        <!-- Flat Number -->
                        <div v-if="address.flat_number" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-home-variant</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">Flat/Building:</span>
                            <span class="text-caption ml-1">{{ address.flat_number }}</span>
                          </div>
                        </div>

                        <!-- Address -->
                        <div v-if="address.address" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-map-marker</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">Address:</span>
                            <span class="text-caption ml-1">{{ address.address }}</span>
                          </div>
                        </div>

                        <!-- Locality -->
                        <div v-if="address.locality" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-sign-direction</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">Locality:</span>
                            <span class="text-caption ml-1">{{ address.locality }}</span>
                          </div>
                        </div>

                        <!-- City -->
                        <div v-if="address.city" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-city</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">City:</span>
                            <span class="text-caption ml-1">{{ address.city }}</span>
                          </div>
                        </div>

                        <!-- State -->
                        <div v-if="address.state" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-map</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">State:</span>
                            <span class="text-caption ml-1">{{ address.state }}</span>
                          </div>
                        </div>

                        <!-- Pincode -->
                        <div v-if="address.pincode" class="d-flex align-start mb-2">
                          <v-icon x-small class="mr-2 mt-1 grey--text">mdi-mailbox</v-icon>
                          <div class="flex-grow-1">
                            <span class="text-caption font-weight-bold grey--text">Pincode:</span>
                            <span class="text-caption ml-1">{{ address.pincode }}</span>
                          </div>
                        </div>

                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Empty State -->
            <div v-else class="d-flex flex-column align-center justify-center py-12">
              <v-icon size="80" color="grey lighten-3">mdi-map-marker-off</v-icon>
              <h3 class="text-h6 grey--text mt-4 mb-2">No Addresses Found</h3>
              <p class="grey--text text-center mb-0">
                This patient hasn't added any addresses yet.
              </p>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="py-2 px-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="closeAddressDialog">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
export default {
  name: "patientsPage",
  components: { DeleteConfirmationDialog },
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        position_id: 2, // PATIENT position (confirmed by getMasters)
        customer_search: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: null,
      dateMenu: false,
      dateRange: [],
      is_deleted_user: false,
      deleteDialog: false,
      itemToDelete: null,
      deleteLoading: false,
      showPermanentDeleteDialog: false,
      permanentDeleteTarget: null,
      permanentDeleteError: "",
      showRestoreDialog: false,
      restoreTarget: null,
      restoreError: "",
      isRestoring: false,
      isPermanentDeleting: false,
      users: [],
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      positions: [],
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Patient Info",
          value: "user_info",
          sortable: false,
          width: "160px",
        },
        { text: "Customer", value: "customer_info", sortable: false, width: "220px" },
        { text: "Status", value: "is_active", sortable: false, width: "80px" },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "110px",
          align: "center",
        },
      ],
      isLoading: false,
      isExcelLoading: false,
      showAddressDialog: false,
      loadingAddresses: false,
      selectedPatient: null,
      patientAddresses: [],
      // Delete confirmation
      showDeleteDialog: false,
      patientToDelete: null,
      deleteReason: '',
      deleteReasonError: '',
      isDeletingPatient: false,
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active
      );
    },
  },
  watch: {
    page: "getData",
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();    // fire data request first
    this.getMasters(); // fetch masters concurrently for dropdowns
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=user`)
        .then((masters) => {
          masters = masters.data;
          this.positions =
            masters.positions?.map((position) => ({
              text: position.name,
              value: position.id,
            })) || [];

          const patientPosition = masters.positions?.find(p => p.name === 'PATIENT');
          if (patientPosition && this.filter.position_id !== patientPosition.id) {
            // position_id changed from default — reload with correct ID
            this.filter.position_id = patientPosition.id;
            this.getData();
          }
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },
    async getData() {
      console.log("Get Data - Patients");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword
        ? this.filter.search_keyword
        : "";
      this.filter.is_active = this.filter.is_active
        ? this.filter.is_active
        : "";
      this.filter.position_id = this.filter.position_id
        ? this.filter.position_id
        : "";
      let query =
        "lite=1&page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&position_id=" +
        this.filter.position_id +
        "&search_keyword=" +
        this.filter.search_keyword +
        "&parent_patient_id=not_null" +
        "&customer_search=" +
        (this.filter.customer_search || "");
      await axios
        .get(`users?${query}`)
        .then((response) => {
          this.users = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });
      this.isLoading = false;
    },
    async Delete_this(id) {
      this.delete_user = true;
      await axios
        .post(`/users/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_user = false;
          this.is_deleted_user = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },

    openDeleteDialog(patient) {
      this.patientToDelete = patient;
      this.deleteReason = '';
      this.deleteReasonError = '';
      this.showDeleteDialog = true;
    },

    closeDeleteDialog() {
      if (this.isDeletingPatient) return;
      this.showDeleteDialog = false;
      this.patientToDelete = null;
      this.deleteReason = '';
      this.deleteReasonError = '';
    },

    openDeleteConfirmDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      this.deleteLoading = true;
      try {
        await axios.post(`/users/delete/${this.encryptIt(this.itemToDelete.id)}`);
        this.deleteDialog = false;
        this.itemToDelete = null;
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.deleteLoading = false;
    },

    async confirmDeletePatient() {
      if (!this.patientToDelete) return;
      const reason = (this.deleteReason || '').trim();
      if (!reason) {
        this.deleteReasonError = 'Reason is required';
        return;
      }
      this.deleteReasonError = '';
      this.isDeletingPatient = true;
      try {
        await axios.post(
          `/users/delete/${this.encryptIt(this.patientToDelete.id)}`,
          { reason }
        );
        this.$toast?.success?.('Patient deleted successfully');
        this.showDeleteDialog = false;
        this.patientToDelete = null;
        this.deleteReason = '';
        await this.getData();
      } catch (error) {
        console.error('Error deleting patient:', error);
        const msg = error?.response?.data?.message || 'Failed to delete patient';
        this.$toast?.error?.(msg);
        if (error?.response?.status === 401) {
          window.location.href = '/auth/login';
        }
      } finally {
        this.isDeletingPatient = false;
      }
    },

    async generateExcel() {
      this.isExcelLoading = true;
      try {
        const params = new URLSearchParams();
        if (this.filter.position_id) params.append('position_id', this.filter.position_id);
        if (this.filter.search_keyword) params.append('search_keyword', this.filter.search_keyword);
        if (this.filter.customer_search) params.append('customer_search', this.filter.customer_search);
        if (this.filter.is_active !== '' && this.filter.is_active !== null) params.append('is_active', this.filter.is_active);
        params.append('show_deleted', this.filter.show_deleted ? 1 : 0);

        const response = await axios.get(`users/excel/patients?${params.toString()}`, {
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toISOString().slice(0, 10);
        let filename = `patients_export_${today}.xlsx`;
        const cd = response.headers['content-disposition'];
        if (cd) {
          const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(cd);
          if (match && match[1]) filename = match[1].replace(/['"]/g, '');
        }
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Excel export failed:', error);
        this.$toast.error('Failed to export patients');
      } finally {
        this.isExcelLoading = false;
      }
    },

    isDeletePage() {
      let response = false;
      if (this.$route.query.open_trash) {
        response = true;
      }
      return response;
    },

    openTrash() {
      this.$router
        .push({ query: { ...this.$route.query, open_trash: 1 } })
        .catch(() => {});
    },

    closeTrash() {
      const query = { ...this.$route.query };
      delete query.open_trash;
      this.$router.push({ query }).catch(() => {});
    },

    openRestoreConfirm(item) {
      this.restoreTarget = item;
      this.restoreError = '';
      this.showRestoreDialog = true;
    },

    async confirmRestore() {
      if (!this.restoreTarget) return;
      this.isRestoring = true;
      this.restoreError = '';
      try {
        await axios.post(`/users/restore/${this.encryptIt(this.restoreTarget.id)}`);
        this.showRestoreDialog = false;
        this.restoreTarget = null;
        this.$toast?.success?.('Patient restored successfully.');
        await this.getData();
      } catch (error) {
        console.error('Error restoring patient:', error);
        if (error?.response?.status === 401) {
          window.location.href = '/auth/login';
        } else {
          this.restoreError = error?.response?.data?.message || 'Failed to restore this patient.';
        }
      } finally {
        this.isRestoring = false;
      }
    },

    openPermanentDeleteConfirm(item) {
      this.permanentDeleteTarget = item;
      this.permanentDeleteError = '';
      this.showPermanentDeleteDialog = true;
    },

    async confirmPermanentDelete() {
      if (!this.permanentDeleteTarget) return;
      this.isPermanentDeleting = true;
      this.permanentDeleteError = '';
      try {
        await axios.post(`/users/permanent-delete/${this.encryptIt(this.permanentDeleteTarget.id)}`);
        this.showPermanentDeleteDialog = false;
        this.permanentDeleteTarget = null;
        await this.getData();
      } catch (error) {
        console.error('Error permanently deleting patient:', error);
        if (error?.response?.status === 401) {
          window.location.href = '/auth/login';
        } else {
          this.permanentDeleteError =
            error?.response?.data?.message || 'Failed to permanently delete this record.';
        }
      } finally {
        this.isPermanentDeleting = false;
      }
    },

    // Enhanced functionality methods
    getFullName(user) {
      if (!user) return "Unknown User";
      const firstName = user.first_name || "";
      const lastName = user.last_name || "";
      return `${firstName} ${lastName}`.trim() || user.email || "No name";
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";
      const base = (this.mediaUrl || '').replace(/\/$/, '');
      return `${base}/${imagePath}`;
    },

    getWalletColor(amount) {
      const wallet = parseFloat(amount) || 0;
      if (wallet >= 1000) return "success";
      if (wallet >= 500) return "warning";
      return "error";
    },

    formatCurrency(amount) {
      const wallet = parseFloat(amount) || 0;
      return wallet.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.is_active = "";
      // Keep position_id as PATIENT
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
        this.getData();
      }
      this.jumpToPage = null;
    },

    // Address-related methods
    hasUserProfileAddress(user) {
      return !!(user.flat_number || user.address || user.locality || user.city || user.state || user.pincode);
    },

    getUserProfileAddress(user) {
      const parts = [];
      if (user.flat_number) parts.push(user.flat_number);
      if (user.address) parts.push(user.address);
      if (user.locality) parts.push(user.locality);
      if (user.city) parts.push(user.city);
      if (user.state) parts.push(user.state);
      if (user.pincode) parts.push(user.pincode);
      return parts.join(', ') || 'Address available';
    },

    getAddressText(address) {
      if (!address) return '';
      const parts = [];
      if (address.flat_number) parts.push(address.flat_number);
      if (address.address) parts.push(address.address);
      if (address.locality) parts.push(address.locality);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.pincode) parts.push(address.pincode);
      
      // If no parts, try to use available fields
      if (parts.length === 0) {
        const availableParts = [];
        if (address.receiver_name) availableParts.push(address.receiver_name);
        if (address.receiver_phone) availableParts.push(address.receiver_phone);
        return availableParts.join(' - ') || 'Address available';
      }
      
      return parts.join(', ');
    },

    getFullAddressText(address) {
      if (!address) return '';
      const parts = [];
      if (address.flat_number) parts.push(address.flat_number);
      if (address.address) parts.push(address.address);
      if (address.locality) parts.push(address.locality);
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.pincode) parts.push(address.pincode);
      
      if (parts.length === 0) {
        return 'Complete address not available';
      }
      
      return parts.join(', ');
    },

    getAddressTypeColor(type) {
      const colors = {
        'HOME': 'success',
        'WORK': 'primary',
        'OTHER': 'warning'
      };
      return colors[type] || 'grey';
    },

    formatAddressType(type) {
      return type ? type.charAt(0) + type.slice(1).toLowerCase() : '';
    },

    async viewAddresses(patient) {
      this.selectedPatient = patient;
      this.showAddressDialog = true;
      this.loadingAddresses = true;
      
      try {
        const response = await axios.get(`/user_addresses?user_id=${patient.id}`);
        this.patientAddresses = response.data.data || [];
      } catch (error) {
        console.error('Error loading addresses:', error);
        this.patientAddresses = [];
        // Show error message
        this.$store.dispatch('errors/setError', {
          message: 'Failed to load patient addresses',
          type: 'error'
        });
      } finally {
        this.loadingAddresses = false;
      }
    },

    closeAddressDialog() {
      this.showAddressDialog = false;
      this.selectedPatient = null;
      this.patientAddresses = [];
      this.loadingAddresses = false;
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Compact Menu Styling */
.v-menu__content {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.v-list-item {
  min-height: 32px !important;
  padding: 0 12px !important;
}

.v-list-item__icon {
  margin-right: 8px !important;
  width: 16px !important;
}

.v-list-item__title {
  font-size: 0.75rem !important;
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design - Even More Compact on Mobile */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Table Loading Animation */
.v-data-table--loading {
  position: relative;
}

.v-data-table--loading::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  z-index: 10;
}

/* Address Card Styling */
.address-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px !important;
  transition: all 0.2s ease;
}

.address-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}

.address-details .text-caption {
  line-height: 1.4;
}

/* Reduce overall spacing */
.mb-6 {
  margin-bottom: 16px !important;
}

/* Compact row spacing */
[dense] .row {
  margin: -4px !important;
}

[dense] .row > .col,
[dense] .row > [class*="col-"] {
  padding: 4px !important;
}
</style>